import type { FC } from 'react';

import { TRUST_SCORE_MAX, tierFor } from '@/constants/trustScore.constants';

interface TrustScoreBarProps {
  value: number;
  /** Shows the tier name under the number. Off in dense table cells. */
  showTier?: boolean;
}

/**
 * 0-1000 trust score (spec §2.1) with a bar coloured by tier band. Compact:
 * 80px bar + small numeric label below.
 */
const TrustScoreBar: FC<TrustScoreBarProps> = ({ value, showTier = false }) => {
  const clamped = Math.max(0, Math.min(TRUST_SCORE_MAX, value));
  const { label, color } = tierFor(clamped);

  return (
    <div className="flex flex-col gap-1">
      <div
        role="progressbar"
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={TRUST_SCORE_MAX}
        className="h-1 w-20 overflow-hidden rounded-full bg-[var(--color-divider)]"
      >
        <div
          className="h-full rounded-full transition-[width] duration-300"
          style={{ width: `${(clamped / TRUST_SCORE_MAX) * 100}%`, backgroundColor: color }}
        />
      </div>
      <span className="text-[10.5px] font-semibold" style={{ color }}>
        {clamped}/{TRUST_SCORE_MAX}
        {showTier ? ` · ${label}` : ''}
      </span>
    </div>
  );
};

export default TrustScoreBar;
